import {
  ArrowLeft,
  AtSign,
  Download,
  ExternalLink,
  Github,
  Globe2,
  Instagram,
  PackageOpen,
  Twitch,
  UserCheck,
  UserPlus,
  Youtube,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { CreatorActivity } from '../components/CreatorActivity';
import { CreatorAvatar } from '../components/CreatorAvatar';
import { ConnectionBadge } from '../components/ConnectionBadge';
import { DeviceIcon } from '../components/DeviceIcon';
import { Link } from '../components/Link';
import { ProfileTable } from '../components/ProfileTable';
import { ReleaseStatus } from '../components/ReleaseStatus';
import { creatorsBySlug, packsForCreator, profilesForCreator } from '../lib/catalog';
import { activityForCreators } from '../lib/creatorFeed';
import { downloadCountForPack, downloadCountForProfile, formatDownloads } from '../lib/downloadCounts';
import { formatDate, packPath } from '../lib/navigation';
import type { CreatorRecord, CreatorSocialPlatform } from '../types';

interface CreatorPageProps {
  slug: string;
  notify: (message: string, tone?: 'success' | 'error') => void;
  isFollowing: boolean;
  onToggleFollow: () => void;
}

const PROFILES_PER_PAGE = 25;
const INITIAL_PACK_COUNT = 6;

const SOCIAL_ICONS: Record<CreatorSocialPlatform, LucideIcon> = {
  website: Globe2,
  youtube: Youtube,
  twitch: Twitch,
  github: Github,
  instagram: Instagram,
  x: AtSign,
  other: ExternalLink,
};

function fallbackBio(creator: CreatorRecord): string {
  if (creator.name === 'Community archive') {
    return 'Profiles whose original creator was not identified are collected here under the community archive. A curator-written bio can be added later.';
  }
  const remaining = creator.devices.length - Math.min(3, creator.devices.length);
  const coverageItems = creator.devices.slice(0, 3);
  if (remaining > 0) coverageItems.push(`${remaining} more device${remaining === 1 ? '' : 's'}`);
  const coverage = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(coverageItems);
  return `This archive page collects profiles attributed to ${creator.name}, covering ${coverage}. A personal bio has not been added yet.`;
}

export function CreatorPage({ slug, notify, isFollowing, onToggleFollow }: CreatorPageProps) {
  const creator = creatorsBySlug.get(slug);
  const [profilePage, setProfilePage] = useState(1);
  const [showAllPacks, setShowAllPacks] = useState(false);

  if (!creator) {
    return (
      <div className="empty-state">
        <AtSign size={36} />
        <h1>Creator not found</h1>
        <p>This creator page may have moved or the address is incomplete.</p>
        <Link className="button secondary" href="/search">Browse profiles</Link>
      </div>
    );
  }

  const profiles = profilesForCreator(creator).sort(
    (left, right) =>
      Date.parse(right.updatedAt) - Date.parse(left.updatedAt)
      || downloadCountForProfile(right) - downloadCountForProfile(left)
      || left.name.localeCompare(right.name, undefined, { numeric: true }),
  );
  const packs = packsForCreator(creator).sort(
    (left, right) => downloadCountForPack(right) - downloadCountForPack(left) || left.name.localeCompare(right.name),
  );
  const pageCount = Math.max(1, Math.ceil(profiles.length / PROFILES_PER_PAGE));
  const currentPage = Math.min(profilePage, pageCount);
  const pageProfiles = profiles.slice((currentPage - 1) * PROFILES_PER_PAGE, currentPage * PROFILES_PER_PAGE);
  const visiblePacks = showAllPacks ? packs : packs.slice(0, INITIAL_PACK_COUNT);
  const totalDownloads = profiles.reduce((total, profile) => total + downloadCountForProfile(profile), 0);
  const bio = creator.bio ?? fallbackBio(creator);
  const activity = activityForCreators([creator]);

  return (
    <>
      <Link className="back-link" href="/search"><ArrowLeft size={15} />Browse profiles</Link>

      <section className="creator-hero">
        <CreatorAvatar name={creator.name} />

        <div className="creator-identity">
          <div className="creator-name-row">
            <div>
              <h1>{creator.name}</h1>
              <p className="creator-handle">@{creator.username.replace(/^@/, '')}</p>
            </div>
          </div>
          <p className="creator-bio">{bio}</p>
          <div className="tag-row creator-device-tags">
            {creator.devices.slice(0, 5).map((device) => <span className="tag" key={device}>{device}</span>)}
            {creator.devices.length > 5 && <span className="tag">+{creator.devices.length - 5} devices</span>}
          </div>
        </div>

        <div className="creator-actions">
          <button className={`button ${isFollowing ? 'secondary' : 'primary'}`} type="button" onClick={onToggleFollow} aria-pressed={isFollowing} aria-label={`${isFollowing ? 'Unfollow' : 'Follow'} ${creator.name}`}>
            {isFollowing ? <UserCheck size={17} /> : <UserPlus size={17} />}{isFollowing ? 'Following' : 'Follow'}
          </button>
          <small>{isFollowing ? 'Updates appear on your home page.' : 'Follow for new uploads and updates.'}</small>
        </div>
      </section>

      <dl className="creator-stats">
        <div><dt>Profile uploads</dt><dd>{formatDownloads(creator.profileCount)}</dd></div>
        <div><dt>Profile packs</dt><dd>{formatDownloads(creator.packCount)}</dd></div>
        <div><dt>Devices covered</dt><dd>{creator.devices.length}</dd></div>
        <div><dt>Profile downloads</dt><dd>{formatDownloads(totalDownloads)}</dd></div>
        <div><dt>Latest Profile Update</dt><dd>{formatDate(creator.latestUpdatedAt)}</dd></div>
      </dl>

      <CreatorActivity items={activity} returnTo={`/creators/${creator.slug}`} />

      <section className="creator-social-panel" aria-labelledby="creator-links-heading">
        <div>
          <h2 id="creator-links-heading">Creator links</h2>
          <p>Verified websites and social accounts associated with this creator.</p>
        </div>
        {creator.socialLinks.length > 0
          ? (
              <nav className="creator-social-links" aria-label={`${creator.name} social links`}>
                {creator.socialLinks.map((social) => {
                  const SocialIcon = SOCIAL_ICONS[social.platform];
                  return (
                    <a className="button secondary" href={social.url} key={`${social.platform}-${social.url}`} target="_blank" rel="noreferrer">
                      <SocialIcon size={16} />{social.label}<ExternalLink size={13} />
                    </a>
                  );
                })}
              </nav>
            )
          : <p className="creator-social-empty"><AtSign size={17} />No verified social links have been added yet.</p>}
      </section>

      <section className="content-section creator-content-section">
        <div className="section-heading">
          <div><h2>Profile packs</h2><p>{creator.packCount} archive pack{creator.packCount === 1 ? '' : 's'} attributed to {creator.name}.</p></div>
        </div>
        {packs.length > 0
          ? (
              <>
                <div className="creator-pack-grid">
                  {visiblePacks.map((pack) => (
                    <article className="creator-pack-card" key={pack.id}>
                      <DeviceIcon device={pack.device} compact />
                      <div>
                        <p className="pack-device">{pack.device}</p>
                        <h3><Link href={packPath(pack)}>{pack.name}</Link></h3>
                        <p className="connection-summary"><span>{pack.profileCount} profile{pack.profileCount === 1 ? '' : 's'}</span>{pack.connections.map((connection) => <ConnectionBadge key={connection} connection={connection} />)}</p>
                        <div className="creator-pack-release">
                          <ReleaseStatus updated={Date.parse(pack.latestUpdatedAt) > Date.parse(pack.createdAt)} />
                          <time dateTime={pack.latestUpdatedAt}>{formatDate(pack.latestUpdatedAt)}</time>
                        </div>
                      </div>
                      <span><Download size={14} />{formatDownloads(downloadCountForPack(pack))}</span>
                    </article>
                  ))}
                </div>
                {packs.length > INITIAL_PACK_COUNT && (
                  <button className="button ghost creator-show-all" type="button" onClick={() => setShowAllPacks((current) => !current)}>
                    <PackageOpen size={16} />{showAllPacks ? 'Show fewer packs' : `Show all ${packs.length} packs`}
                  </button>
                )}
              </>
            )
          : <div className="empty-note"><PackageOpen size={18} /><p>No profile packs are attributed to this creator yet.</p></div>}
      </section>

      <section className="content-section creator-content-section">
        <div className="section-heading">
          <div><h2>Profile uploads</h2><p>Profiles attributed to {creator.name} in the public archive.</p></div>
        </div>
        <ProfileTable profiles={pageProfiles} notify={notify} returnTo={`/creators/${creator.slug}`} showReleaseStatus />
        {pageCount > 1 && (
          <nav className="pagination creator-pagination" aria-label="Creator profile pages">
            <button className="button ghost" type="button" disabled={currentPage <= 1} onClick={() => setProfilePage((page) => Math.max(1, page - 1))}>Previous</button>
            <span>{(currentPage - 1) * PROFILES_PER_PAGE + 1}–{Math.min(currentPage * PROFILES_PER_PAGE, profiles.length)} of {formatDownloads(profiles.length)}</span>
            <button className="button ghost" type="button" disabled={currentPage >= pageCount} onClick={() => setProfilePage((page) => Math.min(pageCount, page + 1))}>Next</button>
          </nav>
        )}
      </section>
    </>
  );
}
